import { useEffect, useState } from "react";
import { defaultContent } from "./defaultContent";
import { mergeMediaKitContent } from "./mergeContent";
import type { CmsResponse, MediaKitContent } from "./types";

export type MediaKitLoadSource = "cms" | "fallback";

type State = {
  content: MediaKitContent;
  loading: boolean;
  error: string | null;
  /** Whether the visible content came from published CMS or brand seed defaults */
  source: MediaKitLoadSource;
  /** True when API returned meta.fallback / empty published row */
  usedFallback: boolean;
  /** DB title for this kit (e.g. Fashion) — unique per vertical */
  kitTitle: string | null;
  isGeneral: boolean;
};

/**
 * Load published media kit.
 * @param slug - null/undefined → general kit (`/api/mediakit` without slug)
 *             - string → vertical kit by slug
 */
export function useMediaKitContent(slug?: string | null): State {
  const normalized = slug?.trim() || "";

  const [state, setState] = useState<State>({
    content: defaultContent,
    loading: true,
    error: null,
    source: "fallback",
    usedFallback: true,
    kitTitle: null,
    isGeneral: !normalized,
  });

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 8000);

    (async () => {
      try {
        const qs = normalized
          ? `?slug=${encodeURIComponent(normalized)}`
          : "";
        const res = await fetch(`/api/mediakit${qs}`, {
          cache: "no-store",
          signal: controller.signal,
        });
        if (!res.ok) {
          throw new Error(`CMS respondió ${res.status}`);
        }
        const payload = (await res.json()) as {
          data?: (MediaKitContent & {
            _meta?: { title?: string; isGeneral?: boolean };
          }) | null;
          meta?: {
            fallback?: boolean;
            slug?: string;
            isGeneral?: boolean;
            title?: string | null;
          };
        };
        if (cancelled) return;
        if (!payload?.data || typeof payload.data !== "object") {
          // No published row — brand default (only meaningful for general)
          setState({
            content: defaultContent,
            loading: false,
            error: null,
            source: "fallback",
            usedFallback: true,
            kitTitle: normalized ? normalized : "General",
            isGeneral: !normalized,
          });
          return;
        }
        const data = payload.data as MediaKitContent & {
          _meta?: { title?: string; isGeneral?: boolean };
        };
        const kitTitle =
          payload.meta?.title ||
          data._meta?.title ||
          (payload.meta?.isGeneral || data._meta?.isGeneral
            ? "General"
            : data.slug || normalized || "Media Kit");
        const isGeneral =
          typeof payload.meta?.isGeneral === "boolean"
            ? payload.meta.isGeneral
            : typeof data._meta?.isGeneral === "boolean"
              ? data._meta.isGeneral
              : !normalized;
        setState({
          content: mergeMediaKitContent(data),
          loading: false,
          error: null,
          source: "cms",
          usedFallback: Boolean(payload.meta?.fallback),
          kitTitle,
          isGeneral,
        });
      } catch (error: unknown) {
        if (cancelled) return;
        const timedOut =
          controller.signal.aborted &&
          error instanceof Error &&
          error.name === "AbortError";
        setState({
          content: defaultContent,
          loading: false,
          error: timedOut
            ? "Timeout al cargar el media kit"
            : error instanceof Error
              ? error.message
              : "No se pudo cargar el contenido",
          source: "fallback",
          usedFallback: true,
          kitTitle: normalized ? normalized : "General",
          isGeneral: !normalized,
        });
      } finally {
        window.clearTimeout(timeoutId);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
      window.clearTimeout(timeoutId);
    };
  }, [normalized]);

  return state;
}
